import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { saveScrollPosition, getCurrentScrollPosition, scrollToPosition } from '../modules/Reducers/actions/navigationActions';

export const useScrollPosition = (page, tab = 'main') => {
    const dispatch = useDispatch();
    const scrollPositions = useSelector(state => state.navigation?.scrollPositions);
    const savedPosition = scrollPositions?.[page]?.[tab] || 0;
    const isInitialMount = useRef(true);
    const scrollTimeoutRef = useRef(null);
    const lastSavedPosition = useRef(0);
    const isUnmounting = useRef(false);

    // Save scroll position when component unmounts or page changes
    const saveCurrentPosition = useCallback(() => {
        // Prevent saving during component unmounting to avoid state updates
        if (isUnmounting.current) {
            return;
        }
        
        const currentPosition = getCurrentScrollPosition();
        console.log('useScrollPosition - saveCurrentPosition called, position:', currentPosition, 'page:', page, 'tab:', tab);
        
        // Only save if position has meaningfully changed (prevents unnecessary dispatches)
        const positionDiff = Math.abs(currentPosition - lastSavedPosition.current);
        // Do not save 0 when unmounting (e.g. after navigating away): the new page may have
        // already scrolled to top, which would overwrite the user's feed position
        if (currentPosition > 0 && positionDiff > 10) { // 10px threshold
            lastSavedPosition.current = currentPosition;
            dispatch(saveScrollPosition(page, tab, currentPosition));
        }
    }, [dispatch, page, tab]);

    // Restore scroll position when component mounts
    const restorePosition = useCallback(() => {
        console.log('useScrollPosition - restorePosition called, savedPosition:', savedPosition, 'isInitialMount:', isInitialMount.current);
        if (savedPosition > 0 && isInitialMount.current) {
            console.log('useScrollPosition - scrolling to position:', savedPosition);
            // Small delay to ensure DOM is ready
            // setTimeout(() => {
                scrollToPosition(savedPosition);
            // }, 100);
            isInitialMount.current = false;
        }
    }, [savedPosition]);

    // Set up scroll event listener to save position
    useEffect(() => {
        let ticking = false;
        
        const handleScroll = () => {
            // Prevent scroll handling if component is unmounting
            if (isUnmounting.current) {
                return;
            }
            
            if (!ticking) {
                requestAnimationFrame(() => {
                    // Debounce scroll position saving
                    if (scrollTimeoutRef.current) {
                        clearTimeout(scrollTimeoutRef.current);
                    }
                    
                    scrollTimeoutRef.current = setTimeout(() => {
                        if (isUnmounting.current) return; // Double-check before dispatch
                        
                        const currentPosition = getCurrentScrollPosition();
                        const positionDiff = Math.abs(currentPosition - lastSavedPosition.current);
                        if (currentPosition > 0 && positionDiff > 50) { // Higher threshold for scroll events
                            lastSavedPosition.current = currentPosition;
                            dispatch(saveScrollPosition(page, tab, currentPosition));
                        }
                    }, 100); // Longer debounce for scroll events
                    
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            
            window.removeEventListener('scroll', handleScroll);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
            // Save position when component unmounts (with guard)
            if (!isUnmounting.current) {
                saveCurrentPosition();
            }
            isUnmounting.current = true;
        };
        // Remove saveCurrentPosition from dependencies to prevent infinite loops
        // eslint-disable-next-line react-hooks/exhaustive-deps  
    }, [dispatch, page, tab]);

    // Restore position on mount
    useEffect(() => {
        restorePosition();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedPosition]);

    // Save position before page unload
    useEffect(() => {
        const handleBeforeUnload = () => {
            const currentPosition = getCurrentScrollPosition();
            if (currentPosition > 0) {
                // Direct dispatch to avoid function dependency issues
                dispatch(saveScrollPosition(page, tab, currentPosition));
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
        // Remove saveCurrentPosition dependency to prevent infinite loops
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, page, tab]);

    return {
        savedPosition,
        saveCurrentPosition,
        restorePosition
    };
}; 